import { notificationApi } from '@mathflat/domain/Notification'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import authService from '~/@common/services/auth.service'
import { nativeEventBus } from '~/@common/services/nativeEventBus'
import { localStorageService } from '~/@common/services/storage.service'
import type { RegisterDeviceEventDetail } from '~/@common/types/FCMRegister'

const getPendingFcmToken = (): RegisterDeviceEventDetail | null => {
  const raw = localStorageService.pendingFcmToken
  if (!raw) return null
  try {
    return JSON.parse(raw) as RegisterDeviceEventDetail
  } catch {
    localStorageService.clearFcmDeviceInfo()
    return null
  }
}

const registerDevice = ({ token, deviceId, platformType }: RegisterDeviceEventDetail) => {
  notificationApi
    .registerFcmToken({ token, deviceId, platformType })
    .then(() => {
      localStorageService.clearFcmDeviceInfo()
    })
    .catch(() => {})
}

/**
 * PUSH 알림 기능을 위해
 * FCM 디바이스 등록을 처리하는 hook.
 * 네이티브에서 발생하는 'registerDevice' 이벤트를 수신하여
 * 서버에 FCM 토큰을 등록한다.
 */
export const useFcmDeviceRegistration = () => {
  const location = useLocation()

  useEffect(() => {
    const pending = getPendingFcmToken()

    if (pending && authService.isLoggedIn) {
      registerDevice(pending)
    }

    const unsub = nativeEventBus.subscribe('registerDevice', (e) => {
      const { token, deviceId, platformType } = e.detail
      localStorageService.setFcmDeviceInfo({ token, deviceId, platformType })
      if (authService.isLoggedIn) {
        registerDevice({ token, deviceId, platformType })
      }
    })

    return unsub
  }, [])

  useEffect(() => {
    if (location.pathname === routeName.login) return
    if (!authService.isLoggedIn) return

    const pending = getPendingFcmToken()
    if (!pending) return

    registerDevice(pending)
  }, [location.pathname])
}
